import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Users,
  DollarSign,
  Gift,
  TrendingUp,
  Search,
  Download,
  ToggleLeft,
  ToggleRight,
  Edit3,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Shield,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import PushCampaignManager from '../components/admin/PushCampaignManager';

const AdminReferrals = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    referrer_reward: '',
    referee_reward: '',
    reason: ''
  });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, codesRes, referralsRes] = await Promise.all([
        api.get('/referrals/admin/stats'),
        api.get('/referrals/admin/codes?limit=100'),
        api.get(`/referrals/admin/referrals?limit=100${statusFilter !== 'all' ? `&status=${statusFilter}` : ''}`)
      ]);
      
      setStats(statsRes.data);
      setCodes(codesRes.data.codes || []);
      setReferrals(referralsRes.data.referrals || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load referral data');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleCode = async (code) => {
    setActionLoading(true);
    try {
      const res = await api.patch(`/referrals/admin/codes/${code}/toggle`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle referral code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivateWithRewards = async (referralId) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/referrals/admin/referrals/${referralId}/activate-with-rewards`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate referral');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (referralId, newStatus) => {
    setActionLoading(true);
    try {
      const res = await api.patch(`/referrals/admin/referrals/${referralId}/status`, {
        status: newStatus
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjustReward = async () => {
    if (!selectedReferral || !adjustForm.reason) {
      toast.error('Please provide a reason for the adjustment');
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = { reason: adjustForm.reason };
      if (adjustForm.referrer_reward) {
        payload.referrer_reward = parseFloat(adjustForm.referrer_reward);
      }
      if (adjustForm.referee_reward) {
        payload.referee_reward = parseFloat(adjustForm.referee_reward);
      }
      
      const res = await api.patch(`/referrals/admin/referrals/${selectedReferral.id}/adjust-reward`, payload);
      toast.success('Reward adjusted successfully');
      setShowAdjustDialog(false);
      setSelectedReferral(null);
      setAdjustForm({ referrer_reward: '', referee_reward: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust reward');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async (format = 'json') => {
    try {
      const res = await api.get(`/referrals/admin/export?format=${format}${statusFilter !== 'all' ? `&status=${statusFilter}` : ''}`);
      
      if (format === 'csv') {
        const blob = new Blob([res.data.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `referrals_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `referrals_export_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success(`Exported ${res.data.total} referrals`);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300', icon: Clock },
      active: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', icon: CheckCircle },
      rewarded: { color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', icon: Gift },
      cancelled: { color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', icon: XCircle }
    };
    
    const { color, icon: Icon } = config[status] || config.pending;
    
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredCodes = codes.filter(code => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      code.code.toLowerCase().includes(term) ||
      code.user_email.toLowerCase().includes(term) ||
      code.user_name.toLowerCase().includes(term)
    );
  });

  const filteredReferrals = referrals.filter(ref => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      ref.referrer_email.toLowerCase().includes(term) ||
      ref.referrer_name.toLowerCase().includes(term) ||
      ref.referred_email.toLowerCase().includes(term) ||
      (ref.referred_name && ref.referred_name.toLowerCase().includes(term)) ||
      ref.referral_code.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-7xl" data-testid="admin-referrals-page">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Shield className="h-8 w-8 text-emerald-600" />
              Referral Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Admin panel for managing referral codes and rewards
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => handleExport('csv')}
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              variant="outline" 
              onClick={fetchData}
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_referral_codes}</p>
                    <p className="text-xs text-muted-foreground">Total Codes</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_referrals}</p>
                    <p className="text-xs text-muted-foreground">Total Referrals</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${stats.total_rewards_distributed.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Distributed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                    <Gift className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${stats.pending_rewards.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Status Breakdown */}
        {stats && (
          <div className="grid grid-cols-4 gap-2 mb-6">
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
              <p className="text-lg font-semibold text-amber-700 dark:text-amber-300">{stats.pending_referrals}</p>
              <p className="text-xs text-amber-600 dark:text-amber-400">Pending</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
              <p className="text-lg font-semibold text-blue-700 dark:text-blue-300">{stats.active_referrals}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400">Active</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
              <p className="text-lg font-semibold text-green-700 dark:text-green-300">{stats.rewarded_referrals}</p>
              <p className="text-xs text-green-600 dark:text-green-400">Rewarded</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-3 text-center">
              <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                {stats.total_referrals - stats.pending_referrals - stats.active_referrals - stats.rewarded_referrals}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Cancelled</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="referrals" className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <TabsList>
              <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals</TabsTrigger>
              <TabsTrigger value="codes" data-testid="tab-codes">Referral Codes</TabsTrigger>
              <TabsTrigger value="campaigns" data-testid="tab-campaigns">
                <Bell className="w-4 h-4 mr-1" />
                Campaigns
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="rewarded">Rewarded</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>All Referrals</CardTitle>
                <CardDescription>
                  Manage referral relationships and rewards
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Referrer</TableHead>
                        <TableHead>Referred User</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Rewards</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReferrals.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            No referrals found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredReferrals.map((ref) => (
                          <TableRow key={ref.id} data-testid={`referral-row-${ref.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{ref.referrer_name}</p>
                                <p className="text-xs text-muted-foreground">{ref.referrer_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{ref.referred_name || 'N/A'}</p>
                                <p className="text-xs text-muted-foreground">{ref.referred_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <code className="text-xs bg-muted px-2 py-1 rounded">{ref.referral_code}</code>
                            </TableCell>
                            <TableCell>{getStatusBadge(ref.status)}</TableCell>
                            <TableCell className="text-right">
                              <div>
                                <p className="text-sm">${ref.referrer_reward} / ${ref.referee_reward}</p>
                                <p className="text-xs text-muted-foreground">Referrer / Referee</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm">{new Date(ref.created_at).toLocaleDateString()}</p>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {ref.status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleActivateWithRewards(ref.id)}
                                      disabled={actionLoading}
                                      data-testid={`activate-btn-${ref.id}`}
                                    >
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Activate
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setSelectedReferral(ref);
                                        setAdjustForm({
                                          referrer_reward: ref.referrer_reward.toString(),
                                          referee_reward: ref.referee_reward.toString(),
                                          reason: ''
                                        });
                                        setShowAdjustDialog(true);
                                      }}
                                      data-testid={`adjust-btn-${ref.id}`}
                                    >
                                      <Edit3 className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-destructive hover:text-destructive"
                                      onClick={() => handleUpdateStatus(ref.id, 'cancelled')}
                                      disabled={actionLoading}
                                      data-testid={`cancel-btn-${ref.id}`}
                                    >
                                      <XCircle className="h-3 w-3" />
                                    </Button>
                                  </>
                                )}
                                {ref.status === 'active' && (
                                  <Badge variant="outline" className="text-xs">
                                    Rewards Distributed
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Codes Tab */}
          <TabsContent value="codes">
            <Card>
              <CardHeader>
                <CardTitle>Referral Codes</CardTitle>
                <CardDescription>
                  View and manage all referral codes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead className="text-center">Total</TableHead>
                        <TableHead className="text-center">Successful</TableHead>
                        <TableHead className="text-right">Earnings</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCodes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No referral codes found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredCodes.map((code) => (
                          <TableRow key={code.id} data-testid={`code-row-${code.code}`}>
                            <TableCell>
                              <code className="font-mono text-sm bg-muted px-2 py-1 rounded">{code.code}</code>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{code.user_name}</p>
                                <p className="text-xs text-muted-foreground">{code.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{code.total_referrals}</TableCell>
                            <TableCell className="text-center">{code.successful_referrals}</TableCell>
                            <TableCell className="text-right font-medium">${code.total_earnings.toFixed(2)}</TableCell>
                            <TableCell className="text-center">
                              {code.is_active ? (
                                <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                                  Active
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
                                  Disabled
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {code.created_at ? new Date(code.created_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleCode(code.code)}
                                disabled={actionLoading}
                                data-testid={`toggle-code-${code.code}`}
                              >
                                {code.is_active ? (
                                  <ToggleRight className="h-4 w-4 text-green-600" />
                                ) : (
                                  <ToggleLeft className="h-4 w-4 text-red-600" />
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Push Campaigns Tab */}
          <TabsContent value="campaigns">
            <PushCampaignManager />
          </TabsContent>
        </Tabs>

        {/* Adjust Reward Dialog */}
        <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adjust Referral Rewards</DialogTitle>
              <DialogDescription>
                Modify the reward amounts for this pending referral. This action will be logged.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Referrer Reward ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={adjustForm.referrer_reward}
                  onChange={(e) => setAdjustForm({ ...adjustForm, referrer_reward: e.target.value })}
                  placeholder="10.00"
                  data-testid="adjust-referrer-reward"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Referee Reward ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={adjustForm.referee_reward}
                  onChange={(e) => setAdjustForm({ ...adjustForm, referee_reward: e.target.value })}
                  placeholder="5.00"
                  data-testid="adjust-referee-reward"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Reason for Adjustment *</label>
                <Input
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                  placeholder="e.g., Promotional bonus, Error correction"
                  data-testid="adjust-reason"
                />
              </div>
              {selectedReferral && (
                <div className="bg-muted rounded-lg p-3 text-sm">
                  <p><strong>Referral ID:</strong> {selectedReferral.id}</p>
                  <p><strong>Current:</strong> ${selectedReferral.referrer_reward} / ${selectedReferral.referee_reward}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAdjustReward} disabled={actionLoading || !adjustForm.reason}>
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminReferrals;
